import { selectDocuments, selectCurrentSelectedFileMetaData } from 'common/selectors';
import React, { useCallback, useRef, useEffect, useContext } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector } from 'react-redux';

// Create a context to pass the data length around
export const ScrollDataContext = React.createContext<{ dataLength: number }>({ dataLength: 0 });

// Create the CustomScrollbars component that will be used by CustomScrollbarsVirtualList
export const CustomScrollbars = ({
  onScroll,
  style,
  children,
  ...rest
}: {
  onScroll?: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
  [key: string]: any;
}) => {
  const scrollbarRef = useRef<any>(null);

  return (
    <PerfectScrollbar
      containerRef={(ref) => {
        scrollbarRef.current = ref;
      }}
      style={style}
      options={{ minScrollbarLength: 20 }}
      onScroll={onScroll}
      {...rest}
    >
      {children}
    </PerfectScrollbar>
  );
};

// The main component that will be used as outerElementType for List
const CustomScrollbarsVirtualList = React.forwardRef<any, any>((props, ref) => {
  const { children, style, onScroll, ...rest } = props;
  const documents = useSelector(selectDocuments);
  const currentSelectedFileMetaData = useSelector(selectCurrentSelectedFileMetaData);
  const scrollbarRef = useRef<any>(null);
  // Get dataLength from context or provide a fallback
  const { dataLength = 0 } = useContext(ScrollDataContext);

  // Track previous dataLength to detect changes
  const prevDataLengthRef = useRef<number>(dataLength);

  // Reset scroll when filtered data is small
  useEffect(() => {
    if (
      dataLength !== prevDataLengthRef.current &&
      dataLength > 0 &&
      dataLength < 10 &&
      scrollbarRef.current
    ) {
      scrollbarRef.current.scrollTop = 0;
    }
    prevDataLengthRef.current = dataLength;
  }, [dataLength]);

  // Handle scrolling when currentSelectedFileMetaData changes
  useEffect(() => {
    if (
      scrollbarRef.current &&
      currentSelectedFileMetaData &&
      Object.keys(currentSelectedFileMetaData).length > 0
    ) {
      let fileIndex = -1;

      // First try folderFileId
      if (currentSelectedFileMetaData.folderFileId) {
        fileIndex = documents.findIndex(
          (item: any) => item.folderFileId === currentSelectedFileMetaData.folderFileId,
        );
      }

      // If not found, try compositeKey
      if (fileIndex === -1 && currentSelectedFileMetaData.compositeKey) {
        fileIndex = documents.findIndex(
          (item: any) => item.compositeKey === currentSelectedFileMetaData.compositeKey,
        );
      }

      // If still not found, try id
      if (fileIndex === -1 && currentSelectedFileMetaData.id) {
        fileIndex = documents.findIndex((item: any) => item.id === currentSelectedFileMetaData.id);
      }

      // Scroll to the found index only if not visible
      if (fileIndex >= 0) {
        const rowHeight = 37;
        const footerHeight = 23; // Account for footer at bottom
        const targetPosition = fileIndex * rowHeight;
        const currentScrollTop = scrollbarRef.current.scrollTop;
        const containerHeight = scrollbarRef.current.clientHeight;
        const visibleTop = currentScrollTop;
        const visibleBottom = currentScrollTop + containerHeight - footerHeight; // Subtract footer height

        // Check if target row is visible (accounting for footer)
        const isVisible =
          targetPosition >= visibleTop && targetPosition + rowHeight <= visibleBottom;

        if (!isVisible) {
          if (fileIndex > 10) {
            const targetScrollTop = 37 * (fileIndex - 10);
            scrollbarRef.current.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth',
            });
          } else {
            scrollbarRef.current.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        }
      }
    }
  }, [currentSelectedFileMetaData, documents]);

  // Handle URL hash-based scrolling whenever documents change or hash changes
  useEffect(() => {
    if (scrollbarRef.current && documents.length > 0) {
      const scrollToIndex = (index: number) => {
        if (index >= 0) {
          const rowHeight = 37;
          const footerHeight = 23; // Account for footer at bottom
          const targetPosition = index * rowHeight;
          const currentScrollTop = scrollbarRef.current.scrollTop;
          const containerHeight = scrollbarRef.current.clientHeight;
          const visibleTop = currentScrollTop;
          const visibleBottom = currentScrollTop + containerHeight - footerHeight; // Subtract footer height

          // Check if target row is visible (accounting for footer)
          const isVisible =
            targetPosition >= visibleTop && targetPosition + rowHeight <= visibleBottom;

          if (!isVisible) {
            // Center the target row in the viewport
            const targetScrollTop = Math.max(0, targetPosition - containerHeight / 2);
            scrollbarRef.current.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth',
            });
          }
        }
      };

      const currentUrlHashVal = window.location.hash;
      const hashFileId = currentUrlHashVal.split('#')[1];

      if (hashFileId) {
        // Try to find the file by folderFileId, compositeKey, or id
        const fileIndex = documents.findIndex(
          (item: any) =>
            item.folderFileId === hashFileId ||
            item.compositeKey === hashFileId ||
            item.id === hashFileId,
        );

        if (fileIndex >= 0) {
          scrollToIndex(fileIndex);
        }
      }
    }
  }, [documents]);

  // Listen for URL hash changes
  useEffect(() => {
    const handleHashChange = () => {
      if (scrollbarRef.current && documents.length > 0) {
        const currentUrlHashVal = window.location.hash;
        const hashFileId = currentUrlHashVal.split('#')[1];

        if (hashFileId) {
          const fileIndex = documents.findIndex(
            (item: any) =>
              item.folderFileId === hashFileId ||
              item.compositeKey === hashFileId ||
              item.id === hashFileId,
          );

          if (fileIndex >= 0) {
            const rowHeight = 37;
            const footerHeight = 23; // Account for footer at bottom
            const targetPosition = fileIndex * rowHeight;
            const currentScrollTop = scrollbarRef.current.scrollTop;
            const containerHeight = scrollbarRef.current.clientHeight;
            const visibleTop = currentScrollTop;
            const visibleBottom = currentScrollTop + containerHeight - footerHeight; // Subtract footer height

            // Check if target row is visible (accounting for footer)
            const isVisible =
              targetPosition >= visibleTop && targetPosition + rowHeight <= visibleBottom;

            if (!isVisible) {
              // Center the target row in the viewport
              const targetScrollTop = Math.max(0, targetPosition - containerHeight / 2);
              scrollbarRef.current.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth',
              });
            }
          }
        }
      }
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Clean up the event listener
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [documents]);

  const refSetter = useCallback(
    (scrollbarsRef) => {
      if (scrollbarsRef) {
        // Store the scrollbar reference
        scrollbarRef.current = scrollbarsRef;

        // Forward the ref
        if (ref) {
          if (typeof ref === 'function') {
            ref(scrollbarsRef);
          } else {
            ref.current = scrollbarsRef;
          }
        }
      }
    },
    [ref],
  );

  return (
    <PerfectScrollbar
      containerRef={refSetter}
      style={style}
      options={{ minScrollbarLength: 20 }}
      onScroll={onScroll}
      {...rest}
    >
      {children}
    </PerfectScrollbar>
  );
});

export default CustomScrollbarsVirtualList;
